import React from "react";
import {
  GitBranch, UserPlus, ArrowUpRight, LogOut, Calculator, ClipboardList,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Input, Field, Select } from "../../components/forms";
import { Stack, Row, Hint, StatusBadge, KVGrid } from "./kit";
import { useHrms, useFetch, fmtDate, fmtMoney, fullName, JOINED_AT_BOUNDS } from "./useHrms";

// HRMS ▸ Lifecycle — onboarding, transitions, resignations, exits & FnF.
// All rows live in one polymorphic events table; tabs filter by type.

const TABS = [
  { key: "onboarding", label: "Onboarding", icon: <UserPlus size={14} /> },
  { key: "transitions", label: "Transitions", icon: <ArrowUpRight size={14} /> },
  { key: "exits", label: "Exits & FnF", icon: <LogOut size={14} /> },
  { key: "tasks", label: "My tasks", icon: <ClipboardList size={14} /> },
];

export default function HrmsLifecycle() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("onboarding");

  return (
    <PageShell
      eyebrow="HRMS"
      title="Lifecycle"
      subtitle="Join → grow → move → exit, with every step owned and dated"
      icon={<GitBranch size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "onboarding" && <OnboardingTab t={t} />}
      {tab === "transitions" && <TransitionsTab t={t} />}
      {tab === "exits" && <ExitsTab t={t} />}
      {tab === "tasks" && <TasksTab t={t} />}
    </PageShell>
  );
}

function EventTable({ t, rows, loading, onToggle, empty }) {
  return (
    <DataTable
      loading={loading}
      columns={[
        { key: "who", header: "Employee", render: (r) => (
          <Row gap={10}>
            <Avatar name={fullName(r.employeeId)} src={r.employeeId?.avatar} size={24} />
            <span>{fullName(r.employeeId)}</span>
          </Row>
        ) },
        { key: "detail", header: "Task", render: (r) => r.detail || cap(r.type) },
        { key: "owner", header: "Owner", render: (r) => fullName(r.ownerId) },
        { key: "due", header: "Due", render: (r) => fmtDate(r.dueDate) },
        { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
        {
          key: "act", header: "", align: "right",
          render: (r) => onToggle && r.status === "pending"
            ? <Button size="sm" variant="ghost" onClick={() => onToggle(r)}>Mark done</Button>
            : null,
        },
      ]}
      rows={rows}
      emptyState={<EmptyState title="Nothing here" description={empty} />}
    />
  );
}

function OnboardingTab({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/lifecycle/events?type=onboarding_task");
  const employees = useFetch(isHr ? "/employees" : null);
  const [form, setForm] = React.useState({ employeeId: "", joiningDate: "" });
  const [notice, setNotice] = React.useState(null);

  const start = async () => {
    if (!form.employeeId) return;
    try {
      const res = await post("/lifecycle/onboarding/start", form);
      setNotice(`Started — ${res.tasks} tasks created.`);
      refresh();
    } catch (e) {
      setNotice(e.message);
    }
  };

  const markDone = async (r) => {
    await patch(`/lifecycle/events/${r._id}`, { status: "done" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      {isHr && (
        <SectionCard title="Start onboarding" subtitle="Instantiates the active template as owned, dated tasks" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Employee">
              <Select value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}>
                <option value="">Select…</option>
                {(employees.data?.employees || []).map((u) => <option key={u.userId} value={u.userId}>{u.name}</option>)}
              </Select>
            </Field>
            <Field label="Joining date">
              <Input
                type="date" min={JOINED_AT_BOUNDS.min} max={JOINED_AT_BOUNDS.max}
                value={form.joiningDate} onChange={(e) => setForm((f) => ({ ...f, joiningDate: e.target.value }))}
              />
            </Field>
            <Button variant="primary" onClick={start}>Start</Button>
          </Row>
          {notice && <div style={{ marginTop: 10 }}><Badge tone="info">{notice}</Badge></div>}
        </SectionCard>
      )}

      <SectionCard title="Onboarding tasks" padding={0}>
        <EventTable t={t} rows={data || []} loading={loading} onToggle={markDone}
          empty="Start an onboarding above — tasks route to HR, the manager and the joiner." />
      </SectionCard>
    </Stack>
  );
}

function TransitionsTab({ t }) {
  const { isHr, post } = useHrms();
  const promotions = useFetch("/lifecycle/events?type=promotion");
  const employees = useFetch(isHr ? "/employees" : null);
  const [form, setForm] = React.useState({ employeeId: "", type: "promotion", toPosition: "", detail: "" });
  const [notice, setNotice] = React.useState(null);

  const create = async () => {
    if (!form.employeeId) return;
    const payload = form.type === "promotion" ? { toPosition: form.toPosition } : form.type === "probation_decision" ? { confirmed: true } : {};
    await post("/lifecycle/transitions", { employeeId: form.employeeId, type: form.type, payload, detail: form.detail || null });
    setNotice("Applied and audited.");
    setForm({ employeeId: "", type: "promotion", toPosition: "", detail: "" });
    promotions.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      {isHr && (
        <SectionCard title="Record a transition" subtitle="Promotions update the role instantly; probation confirmation activates the employee" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Employee">
              <Select value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}>
                <option value="">Select…</option>
                {(employees.data?.employees || []).map((u) => <option key={u.userId} value={u.userId}>{u.name}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {["promotion", "probation_decision", "transfer", "contract_renewal"].map((ty) => <option key={ty} value={ty}>{cap(ty)}</option>)}
              </Select>
            </Field>
            {form.type === "promotion" && (
              <Field label="New position"><Input value={form.toPosition} onChange={(e) => setForm((f) => ({ ...f, toPosition: e.target.value }))} placeholder="Senior Engineer" /></Field>
            )}
            <Field label="Note"><Input value={form.detail} onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))} /></Field>
            <Button variant="primary" onClick={create}>Apply</Button>
          </Row>
          {notice && <div style={{ marginTop: 10 }}><Badge tone="positive">{notice}</Badge></div>}
        </SectionCard>
      )}

      <SectionCard title="Recent transitions" padding={0}>
        <EventTable t={t} rows={promotions.data || []} loading={promotions.loading}
          empty="Promotions, transfers and probation decisions appear here." />
      </SectionCard>
    </Stack>
  );
}

function ExitsTab({ t }) {
  const { isHr, post } = useHrms();
  const resignations = useFetch("/lifecycle/events?type=resignation");
  const fnfs = useFetch(isHr ? "/lifecycle/events?type=fnf_settlement" : null);
  const inbox = useFetch("/approvals/inbox?type=resignation");
  const [fnfForm, setFnfForm] = React.useState({ employeeId: "", workedDaysInLastMonth: "", recovery: "0" });
  const [fnfResult, setFnfResult] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  const decide = async (id, action) => {
    await post(`/approvals/${id}/decide`, { action });
    inbox.refresh(); resignations.refresh();
  };

  const computeFnf = async () => {
    if (!fnfForm.employeeId) return;
    const res = await post(`/lifecycle/fnf/${fnfForm.employeeId}/compute`, {
      workedDaysInLastMonth: Number(fnfForm.workedDaysInLastMonth) || 0,
      recovery: Number(fnfForm.recovery) || 0,
    });
    setFnfResult(res);
    fnfs?.refresh?.();
  };

  const finalize = async (employeeId) => {
    try {
      await post(`/lifecycle/exits/${employeeId}/finalize`, {});
      setNotice("Exit finalized.");
      fnfs?.refresh?.();
    } catch (e) {
      setNotice(e.message);
    }
  };

  const resignedEmployees = [...new Map((resignations.data || []).map((r) => [r.employeeId?._id, r.employeeId])).values()].filter(Boolean);

  return (
    <Stack gap={t.s[4]}>
      {(inbox.data || []).length > 0 && (
        <SectionCard title="Resignations waiting on you" padding={0} actions={<Badge tone="warning">{inbox.data.length}</Badge>}>
          <DataTable
            columns={[
              { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
              { key: "summary", header: "Request", render: (r) => r.summary },
              {
                key: "act", header: "", align: "right",
                render: (r) => (
                  <Row gap={4} justify="flex-end">
                    <Button size="sm" variant="primary" onClick={() => decide(r._id, "approve")}>Accept</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(r._id, "reject")}>Decline</Button>
                  </Row>
                ),
              },
            ]}
            rows={inbox.data}
          />
        </SectionCard>
      )}

      <SectionCard title="Resignations" subtitle="Employees resign from My HR ▸ Requests" padding={0}>
        <EventTable t={t} rows={resignations.data || []} loading={resignations.loading}
          empty="No resignations on record." />
      </SectionCard>

      {isHr && (
        <SectionCard title="Full & final settlement" subtitle="Pro-rata pay + encashable leave − recovery" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Employee">
              <Select value={fnfForm.employeeId} onChange={(e) => setFnfForm((f) => ({ ...f, employeeId: e.target.value }))}>
                <option value="">Select…</option>
                {resignedEmployees.map((u) => <option key={u._id} value={u._id}>{fullName(u)}</option>)}
              </Select>
            </Field>
            <Field label="Days worked (last month)"><Input type="number" value={fnfForm.workedDaysInLastMonth} onChange={(e) => setFnfForm((f) => ({ ...f, workedDaysInLastMonth: e.target.value }))} /></Field>
            <Field label="Recovery (₹)"><Input type="number" value={fnfForm.recovery} onChange={(e) => setFnfForm((f) => ({ ...f, recovery: e.target.value }))} /></Field>
            <Button variant="secondary" leftIcon={<Calculator size={13} />} onClick={computeFnf}>Compute</Button>
            {fnfForm.employeeId && <Button variant="primary" onClick={() => finalize(fnfForm.employeeId)}>Finalize exit</Button>}
          </Row>

          {fnfResult && (
            <div style={{ marginTop: t.s[4] }}>
              <KVGrid items={[
                { label: "Last-month earnings", value: fmtMoney(fnfResult.payload?.earnings) },
                { label: "Leave encashment", value: `${fmtMoney(fnfResult.payload?.encashment)} (${fnfResult.payload?.encashableDays || 0}d)` },
                { label: "Recovery", value: fmtMoney(fnfResult.payload?.recovery) },
                { label: "Net payable", value: fmtMoney(fnfResult.payload?.net) },
              ]} />
            </div>
          )}
          {notice && <div style={{ marginTop: 10 }}><Badge tone="info">{notice}</Badge></div>}
        </SectionCard>
      )}
    </Stack>
  );
}

function TasksTab({ t }) {
  const { patch } = useHrms();
  // Owner-scoped server-side: the caller's default scope only covers rows they
  // are the subject of, so a task they own for someone else never arrives.
  const { data: mine, loading, refresh } = useFetch("/lifecycle/events?status=pending&mine=true");

  const markDone = async (r) => {
    await patch(`/lifecycle/events/${r._id}`, { status: "done" });
    refresh();
  };

  return (
    <SectionCard title="Tasks assigned to you" subtitle="Onboarding items you own, plus your own checklist" padding={0}>
      <EventTable t={t} rows={mine || []} loading={loading} onToggle={markDone}
        empty="No pending lifecycle tasks. New-joiner checklists land here." />
    </SectionCard>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
